import { clamp } from "es-toolkit/compat";

import Logging from "$src/core/services/logging/logging";

const isShortHex = (color: string) => /^#([0-9a-fA-F]{3})$/.test(color);

const isFullHex = (color: string) => /^#([0-9a-fA-F]{6})$/.test(color);

const isHexWithAlpha = (color: string) => /^#([0-9a-fA-F]{8})$/.test(color);

const isRgb = (color: string) =>
  /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.test(color);

const isRgba = (color: string) =>
  /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([\d.]+)\s*\)$/.test(
    color,
  );

const toFixed = (value: number) => {
  if (value === 1 || value === 0) return value.toFixed(0);
  // Max 2 decimal places, then remove trailing zeros and possibly trailing decimal
  return value.toFixed(2).replace(/\.?0+$/, "");
};

const opacity = (color: string, rawValue: number) => {
  try {
    const value = clamp(rawValue, 0, 1);

    const alphaHex = Math.round(value * 255)
      .toString(16)
      .padStart(2, "0");

    if (isShortHex(color)) {
      return `#${color.toLowerCase().slice(1).repeat(2)}${alphaHex}`;
    }

    if (isFullHex(color)) {
      return `${color.toLowerCase()}${alphaHex}`;
    }

    if (isHexWithAlpha(color)) {
      return `#${color.slice(1, 7).toLowerCase()}${alphaHex}`;
    }

    const alpha = toFixed(value);

    if (isRgb(color)) {
      const [r, g, b] = color.slice(4, -1).split(",").map(Number);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    if (isRgba(color)) {
      const [r, g, b] = color.slice(5, -1).split(",").map(Number);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Unistyles emits CSS vars for theme colors on web; color-mix is the only way to alpha them
    if (color.startsWith("var(")) {
      const percent = (value * 100).toFixed(2).replace(/\.?0+$/, "");
      return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
    }
  } catch (error) {
    Logging.error(error);
    return color;
  }

  Logging.warn(`Unsupported color format: ${color}`);
  return color;
};

export default opacity;
